import { RefObject, useCallback } from "react";

import BulkActions from "components/BulkActions";
import {
  BulkActionItemID,
  BulkActionResultTabs,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import useBulkActions from "components/BulkActions/useBulkActions";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import { Run, Stack } from "types/generated";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import { type BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import { getDefaultOnContinueWith } from "components/BulkActions/Drawer/ResultsStep/helpers";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useAnalytics from "hooks/useAnalytics";
import { BulkActionResultsStepContinueWith } from "components/BulkActions/Drawer/ResultsStep/types";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";

import useStackRunsBulkActions, {
  type StackRunsMutationVariables,
} from "./useStackRunsBulkActions";
import { StackRunsBulkActionsAnalyticsPayload } from "./types";
import StackRunsBulkActionsResultItem from "./ResultItem";
import StackRunsBulkActionsConfirmationView from "./ConfirmationView";
import StackRunsBulkActionsSelectedItemsView from "./SelectedItemsView";
import StackRunsBulkActionsConfirmActionForm from "./ConfirmActionForm";

type StackRunsBulkActionsProps = {
  listRef: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  runsMap: Map<BulkActionItemID, Run>;
  stack: Stack;
  onBulkResetAll: () => void;
  onBulkContinueWith: (continueWith: Set<BulkActionItemID>) => void;
  onItemDismiss: (id: BulkActionItemID) => void;
  onFinish: () => Promise<unknown>;
};

const StackRunsBulkActions = ({
  listRef,
  selectedSet,
  runsMap,
  stack,
  onBulkResetAll,
  onBulkContinueWith,
  onItemDismiss,
  onFinish,
}: StackRunsBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const stackRunsBulkActions = useStackRunsBulkActions();
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageStack.StacksRunView,
  });
  const {
    selectedBulkAction,
    resetSelectedAction,
    availableActions,
    itemsPerAction,
    actionsPerItem,
    selectedEntityItems,
    applicableSelectedEntityItems,
    skippedSelectedEntityItems,
  } = useBulkActions({
    actions: stackRunsBulkActions,
    selectedSet,
    entityItemsMap: runsMap,
  });

  const onItemTrigger = useCallback(
    (
      id: BulkActionItemID,
      mutationVariables: StackRunsMutationVariables
    ): Promise<BulkActionItemTriggerResult> => {
      const run = runsMap.get(id);
      if (!run || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(run).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(run, mutationVariables);
    },
    [runsMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage: AnalyticsPageStack.StacksRunView,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<Run>(bulkActionItemResults);

  const handleContinueWith = useCallback(
    (continueWith: BulkActionResultsStepContinueWith) => {
      getDefaultOnContinueWith(resultsTabItems, onBulkContinueWith)(continueWith);
      resetSelectedAction();
      resetBulkActionsExecutionQueue();
    },
    [onBulkContinueWith, resetSelectedAction, resetBulkActionsExecutionQueue, resultsTabItems]
  );

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
    stopExecution();
  }, [
    onBulkResetAll,
    resetSelectedAction,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (
      mutationVariables: StackRunsMutationVariables,
      analyticsPayload: StackRunsBulkActionsAnalyticsPayload
    ) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);
        triggerExecution(items, mutationVariables, skippedItems);
        saveEntityMap(runsMap);
        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      runsMap,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      trackSegmentAnalyticsEvent,
      triggerExecution,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onClose={handleClose}
      onContinueWith={handleContinueWith}
      isExecutingBulkActions={!!resultsTabItems[BulkActionResultTabs.Pending].count}
    >
      <BulkActionsFloatingBar virtualizedListContainerRef={listRef} isFixed>
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageStack.StacksRunView}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <StackRunsBulkActionsConfirmActionForm
              stack={stack}
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageStack.StacksRunView}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <StackRunsBulkActionsSelectedItemsView
              stack={stack}
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}
          {selectedBulkAction && (
            <StackRunsBulkActionsConfirmationView
              stack={stack}
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {(props) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={AnalyticsPageStack.StacksRunView}
              />
              <BulkActionsDrawerResultsStepBody {...props} counter={resultsTabItems}>
                {({ currentTab }) =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((run) => (
                        <StackRunsBulkActionsResultItem
                          key={run.id}
                          tab={currentTab}
                          stack={stack}
                          item={run}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(run.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>
              <BulkActionsDrawerResultsStepFooter
                {...props}
                bulkActionItemResults={bulkActionItemResults}
                onContinueEnabled
                stopExecution={stopExecution}
                analyticsPage={AnalyticsPageStack.StacksRunView}
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default StackRunsBulkActions;
